// EmployeeManagementPage.js
import React, { useEffect, useRef, useState } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
// Only global theme variables and the legacy dialog styles are left in this
// file; importing it here keeps its place in the cascade unchanged.
import './EmployeeManagementPage.module.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserProfile } from '../../contexts/UserProfileContext';
// Global dark-mode sheet; the legacy dialogs below (and other pages) rely on it
import './EmployeeManagementDarkMode.css';

// Imported modal components
import EditEmployeeModal from './EditEmployeeModal';
import FileUploadModal from './FileUploadModal';
import ProjectsModal from './ProjectsModal';
import AddEmployeeModal from './AddEmployeeModal';
import CreateUserModal from './CreateUserModal';
import ExitDateModal from './ExitDateModal';
import EmployeeCardModal from './EmployeeCardModal';
import MissingDocumentsModal from './MissingDocumentsModal';
import PendingLeavesModal from './PendingLeavesModal';
import useMissingEmployeeDocuments from './useMissingEmployeeDocuments';
import useEmployeeDirectory from './useEmployeeDirectory';
import useEmployeeTableColumns from './useEmployeeTableColumns';
import usePendingLeaveRequests from './usePendingLeaveRequests';
import useEmployeeAccountStatus from './useEmployeeAccountStatus';
import { downloadEmployeesExcel, updateEmployeeStatusInProjects } from './employeeActions';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import { useTranslation } from '../../i18n/LanguageContext';
import useMediaQuery from '../../hooks/useMediaQuery';
import useViewportFit from '../../hooks/useViewportFit';
import useElementWidth from '../../hooks/useElementWidth';
import EmployeePageHeader from './components/EmployeePageHeader';
import EmployeeStats from './components/EmployeeStats';
import EmployeeToolbar from './components/EmployeeToolbar';
import EmployeeTable from './components/EmployeeTable';
import EmployeeCardList from './components/EmployeeCardList';
import FilterSheet from './components/FilterSheet';
import TablePagination from './components/TablePagination';
import EmployeeDetailPane from './components/EmployeeDetailPane';

// How long a row stays highlighted after jumping to it from a leave request
const FLASH_MS = 2000;
// From this width the employee panel sits beside the table instead of over it
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
// The table card ends this far above the bottom of the screen (Layout's main padding)
const FIT_BOTTOM_GAP = 24;
// Below this the card stops shrinking and the page scrolls instead
const FIT_MIN_HEIGHT = 400;
// Below this list width (phones, a tablet beside the sidebar) the table turns
// into a card list, the page scrolls normally and pages hold a fixed count
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

/**
 * Çalışan Yönetimi (Tailwind). With room for the table, its card may reach the
 * bottom of the screen and pages as many rows as fit there, so neither the
 * page nor the table needs vertical scrolling; a short result ends right under
 * its last row, and the docked employee panel always reaches the bottom. On
 * narrow screens the list is a card list with a filter sheet. The dialogs are
 * still the legacy ones and are rendered outside the animated wrapper, so its
 * transform can't trap their fixed overlays.
 */
const EmployeeManagementPage = () => {
  const { t, language } = useTranslation();
  const { userAuthorization } = useUserProfile();
  const {
    employees,
    setEmployees,
    filteredEmployees,
    pageRows,
    pageStart,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    isFilterActive,
    isLoading,
    hasLoaded,
    statsData,
    currentPage,
    totalPages,
    pageSize,
    setPageSize,
    goToPage,
    revealEmployee,
    refreshEmployees,
  } = useEmployeeDirectory();
  const columns = useEmployeeTableColumns(filters.Active);
  const { pendingLeaveCounts, pendingLeavesList, fetchPendingLeavesList, deleteLeaveRequest } = usePendingLeaveRequests(employees);
  const missingDocs = useMissingEmployeeDocuments();
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const listWidth = useElementWidth(listCardRef, window.innerWidth);
  const compactList = listWidth < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Most the table card may take: down to the bottom of the screen
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const { selectedEmployeeHasUser, isCheckingUser } = useEmployeeAccountStatus(selectedEmployee);
  const [flashEmployeeId, setFlashEmployeeId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showExitDateModal, setShowExitDateModal] = useState(false);
  const [showPendingLeavesModal, setShowPendingLeavesModal] = useState(false);
  const [showMissingDocsModal, setShowMissingDocsModal] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const canOpenFiles = !userAuthorization?.includes('Kısıtlı Admin');

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  // Seçili çalışanla ilgili bir modal açıkken seçim asla düşürülmez; aksi
  // hâlde liste yenilenince modalin kendisi de kapanırdı.
  const isEmployeeModalOpen =
    showEditModal ||
    showCardModal ||
    showFileModal ||
    showProjectsModal ||
    showCreateUserModal ||
    showExitDateModal;

  /**
   * Seçim, tabloda görünen listeyle senkron tutulur.
   *
   * Dosyalar/Projeler gibi modaller kapanırken listeyi yeniliyor
   * (refreshEmployees: önbelleği temizler, ilk sayfaya döner). Seçili çalışan
   * bu yenilemeden sonra listede kalmayabiliyor; eskiden seçim state'i olduğu
   * gibi durduğu için üstteki ada "biri seçili" izlenimi vermeye devam ediyor
   * ve ada üzerindeki düğmeler artık ekranda olmayan bir kayıt üzerinde
   * işlem yapıyordu. Artık:
   *   - çalışan listede yoksa seçim (ve ada) kalkar,
   *   - listede ama verisi tazelenmişse ada yeni veriyi gösterir.
   */
  useEffect(() => {
    if (!selectedEmployee || isEmployeeModalOpen) return;

    const current = filteredEmployees.find((emp) => emp.id === selectedEmployee.id);
    if (!current) {
      setSelectedEmployee(null);
      return;
    }
    if (current !== selectedEmployee) {
      setSelectedEmployee(current);
    }
  }, [filteredEmployees, selectedEmployee, isEmployeeModalOpen]);

  // Escape clears the selection, unless a dialog, field or popover has it
  useEffect(() => {
    if (!selectedEmployee) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (isEmployeeModalOpen || showAddModal || showPendingLeavesModal || showMissingDocsModal || showFilterSheet) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedEmployee(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEmployee, isEmployeeModalOpen, showAddModal, showPendingLeavesModal, showMissingDocsModal, showFilterSheet]);

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!flashEmployeeId) return undefined;
    const timer = setTimeout(() => setFlashEmployeeId(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashEmployeeId]);

  const handleStatusToggle = async () => {
    if (!selectedEmployee) return;
    const newStatus = selectedEmployee.Active === 1 ? 0 : 1;

    // Eğer çalışan inaktif yapılıyorsa, işten ayrılma tarihi modalını aç
    if (newStatus === 0) {
      setShowExitDateModal(true);
      return;
    }

    // Eğer çalışan aktif yapılıyorsa, direkt güncelle ve projelerini de aktif yap
    try {
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);

      // Önce çalışanı tüm projelerden aktif yap
      await updateEmployeeStatusInProjects(selectedEmployee.id, 'active');

      // Sonra çalışanın durumunu güncelle
      await updateDoc(employeeRef, { Active: newStatus, employeeExitDate: deleteField() });

      // Dinamik olarak seçili çalışanın durumunu güncelle (tarih temizlensin)
      const updatedEmployee = { ...selectedEmployee, Active: newStatus, employeeExitDate: null };
      setSelectedEmployee(updatedEmployee);

      // Çalışanlar listesini de güncelle
      setEmployees(prevEmployees =>
        prevEmployees.map(emp =>
          emp.id === selectedEmployee.id
            ? { ...emp, Active: newStatus, employeeExitDate: null }
            : emp
        )
      );

      // Only activation reaches here; deactivation goes through ExitDateModal
      toast.success(t('employees.toast.activated'));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('employees.toast.statusError'));
    }
  };

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      await downloadEmployeesExcel({
        preparing: t('employees.toast.excelPreparing'),
        success: t('employees.toast.excelSuccess'),
        error: t('employees.toast.excelError'),
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Bekleyen izin talepleri modalını aç
  const openPendingLeavesModal = async () => {
    await fetchPendingLeavesList();
    setShowPendingLeavesModal(true);
  };

  // Talepteki çalışanı seç, sayfasını aç ve satırını kısa süre vurgula
  const focusEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowPendingLeavesModal(false);
    revealEmployee(employee.id);
    setTimeout(() => {
      const employeeRow = document.querySelector(`[data-employee-id="${employee.id}"]`);
      if (employeeRow) {
        employeeRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setFlashEmployeeId(employee.id);
      }
    }, 100);
  };

  // ↑/↓ in the employee panel: step through the list, turning pages as needed
  const selectedIndex = selectedEmployee ? filteredEmployees.findIndex((emp) => emp.id === selectedEmployee.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredEmployees[selectedIndex + offset];
    if (!sibling) return;
    setSelectedEmployee(sibling);
    revealEmployee(sibling.id);
  };

  const detailPaneProps = {
    employee: selectedEmployee,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredEmployees.length } : null,
    pendingLeaveCount: selectedEmployee ? pendingLeaveCounts[selectedEmployee.id] || 0 : 0,
    hasUser: selectedEmployeeHasUser,
    isCheckingUser,
    canOpenFiles,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredEmployees.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedEmployee(null),
    onOpenCard: () => setShowCardModal(true),
    onEdit: () => setShowEditModal(true),
    onToggleStatus: handleStatusToggle,
    onOpenFiles: () => setShowFileModal(true),
    onOpenProjects: () => setShowProjectsModal(true),
    onCreateUser: () => setShowCreateUserModal(true),
  };

  return (
    <>
      <MotionConfig reducedMotion="user">
        <motion.div
          lang={language}
          variants={staggerContainer(0.06)}
          initial="hidden"
          animate="show"
          className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10"
        >
          <motion.div variants={fadeUp}>
            <EmployeePageHeader
              pendingLeaveCount={pendingLeavesList.length}
              missingDocsCount={missingDocs.rows.length}
              isExporting={isExporting}
              onOpenPendingLeaves={openPendingLeavesModal}
              onOpenMissingDocs={() => setShowMissingDocsModal(true)}
              onDownload={handleDownload}
              onAddEmployee={() => setShowAddModal(true)}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <EmployeeStats total={statsData.total} active={statsData.active} inactive={statsData.inactive} ready={hasLoaded} />
          </motion.div>

          <motion.div
            ref={tableAreaRef}
            variants={fadeUp}
            className="tw-flex tw-items-start tw-gap-4"
          >
            <section
              ref={listCardRef}
              aria-label={t('employees.listLabel')}
              style={{ maxHeight: maxCardHeight }}
              className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]"
            >
              <EmployeeToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={filters}
                onFilterChange={handleFilter}
                onClearFilters={clearFilters}
                columns={{
                  visibleColumns: columns.visibleColumns,
                  exitDateAutoHidden: columns.exitDateAutoHidden,
                  onToggle: columns.toggleColumn,
                  onShowAll: columns.showAllColumns,
                  onReset: columns.resetColumns,
                }}
                compact={compactList}
                onOpenFilters={() => setShowFilterSheet(true)}
              />

              {compactList ? (
                <EmployeeCardList
                  rows={pageRows}
                  selectedId={selectedEmployee?.id}
                  flashId={flashEmployeeId}
                  onSelect={handleEmployeeSelect}
                  pendingLeaveCounts={pendingLeaveCounts}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  canClearFilters={Object.keys(filters).length > 0 || Boolean(searchTerm)}
                  onClearFilters={clearFilters}
                />
              ) : (
                <EmployeeTable
                  rows={pageRows}
                  visibleKeys={columns.visibleKeys}
                  getColumnWidth={columns.getColumnWidth}
                  onResizeColumn={columns.setColumnWidth}
                  onResetColumnWidth={columns.resetColumnWidth}
                  filters={filters}
                  onFilterChange={handleFilter}
                  selectedId={selectedEmployee?.id}
                  flashId={flashEmployeeId}
                  onSelect={handleEmployeeSelect}
                  pendingLeaveCounts={pendingLeaveCounts}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  skeletonRows={pageSize}
                  maxCardHeight={maxCardHeight}
                  onCapacityChange={setPageSize}
                  canClearFilters={Object.keys(filters).length > 0 || Boolean(searchTerm)}
                  onClearFilters={clearFilters}
                  onShowColumns={columns.resetColumns}
                />
              )}

              <TablePagination
                rangeStart={pageStart + 1}
                rangeEnd={pageStart + pageRows.length}
                resultCount={filteredEmployees.length}
                totalEmployees={statsData.total}
                isFilterActive={isFilterActive}
                currentPage={currentPage}
                totalPages={totalPages}
                isLoading={isLoading && hasLoaded}
                compact={compactList}
                onPage={goToPage}
              />
            </section>

            {dockPanel && <EmployeeDetailPane docked height={fitHeight} {...detailPaneProps} />}
          </motion.div>
        </motion.div>

        {/* Below the docking width: a drawer over the page (portalled to <body>) */}
        {!dockPanel && <EmployeeDetailPane {...detailPaneProps} />}
      </MotionConfig>

      {showCardModal && selectedEmployee && (
        <EmployeeCardModal
          employee={selectedEmployee}
          onClose={() => setShowCardModal(false)}
          onEdit={() => {
            setShowCardModal(false);
            setShowEditModal(true);
          }}
        />
      )}

      {showEditModal && selectedEmployee && (
        <EditEmployeeModal
          selectedEmployee={selectedEmployee}
          onClose={() => setShowEditModal(false)}
          onUpdate={refreshEmployees}
        />
      )}
      {showFileModal && selectedEmployee && (
        <FileUploadModal
          employee={selectedEmployee}
          onClose={(updatedEmployee, keepOpen) => {
            if (updatedEmployee) {
              // Update the selected employee with the new data
              setSelectedEmployee(updatedEmployee);
              // Update the employee in the employees list
              setEmployees(prevEmployees =>
                prevEmployees.map(emp =>
                  emp.id === updatedEmployee.id ? updatedEmployee : emp
                )
              );
            } else {
              // If no updated employee data, refresh employees
              refreshEmployees();
            }

            // Only close the modal if keepOpen is not true
            if (!keepOpen) {
              setShowFileModal(false);
            }
          }}
        />
      )}
      {showProjectsModal && selectedEmployee && (
        <ProjectsModal
          employee={selectedEmployee}
          onClose={() => setShowProjectsModal(false)}
        />
      )}
      {showAddModal && (
        <AddEmployeeModal
          onClose={() => setShowAddModal(false)}
          onUpdate={refreshEmployees}
        />
      )}
      {showExitDateModal && selectedEmployee && (
        <ExitDateModal
          selectedEmployee={selectedEmployee}
          onClose={() => setShowExitDateModal(false)}
          onUpdate={refreshEmployees}
          onEmployeeUpdate={(updatedEmployee) => {
            // Dinamik olarak seçili çalışanı güncelle
            setSelectedEmployee(updatedEmployee);
            // Çalışanlar listesini de güncelle
            setEmployees(prevEmployees =>
              prevEmployees.map(emp =>
                emp.id === updatedEmployee.id ? updatedEmployee : emp
              )
            );
          }}
        />
      )}

      {showCreateUserModal && selectedEmployee && (
        <CreateUserModal
          employee={selectedEmployee}
          onClose={() => {
              setShowCreateUserModal(false);
              // Re-trigger the user check after modal closes by re-selecting
              if (selectedEmployee) {
                 // Create a shallow copy to ensure useEffect dependency changes
                handleEmployeeSelect({ ...selectedEmployee });
              }
          }}
          // onUserCreated could trigger a refresh or update state directly if needed
        />
      )}

      {showMissingDocsModal && (
        <MissingDocumentsModal
          rows={missingDocs.rows}
          loading={missingDocs.loading}
          error={missingDocs.error}
          onClose={() => setShowMissingDocsModal(false)}
          // Kısıtlı Admin can't open employee files (same as the "Dosyalar" action button)
          onOpenFiles={canOpenFiles ? (employee) => {
            setShowMissingDocsModal(false);
            setSelectedEmployee(employee);
            setShowFileModal(true);
          } : null}
        />
      )}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        filters={filters}
        onFilterChange={handleFilter}
        onClearFilters={clearFilters}
        resultCount={filteredEmployees.length}
      />

      {/* Bekleyen İzin Talepleri Modal */}
      {showPendingLeavesModal && (
        <PendingLeavesModal
          leaves={pendingLeavesList}
          onClose={() => setShowPendingLeavesModal(false)}
          onFocusEmployee={focusEmployee}
          onDelete={deleteLeaveRequest}
        />
      )}

    </>
  );
};

export default EmployeeManagementPage;
